import React, { useState, useEffect } from 'react';
import { trainingSummaryService } from '../services/api';
import { toast } from 'sonner';
import { BarChart3, Users, Clock, CalendarDays, Package } from 'lucide-react';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function RiepilogoOreFormazionePage() {
  const [tab, setTab] = useState('employee');
  const tabs = [
    { key: 'employee', label: 'Per dipendente', icon: Users },
    { key: 'period', label: 'Per periodo', icon: CalendarDays },
    { key: 'topic', label: 'Per tema', icon: BarChart3 },
    { key: 'product', label: 'Per prodotto', icon: Package },
  ];

  return (
    <div className="space-y-4" data-testid="riepilogo-page">
      <h1 className="text-2xl font-bold text-slate-900" data-testid="riepilogo-title">Riepilogo Ore Formazione</h1>
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit flex-wrap" data-testid="riepilogo-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === 'employee' && <ByEmployee />}
      {tab === 'period' && <ByPeriod />}
      {tab === 'topic' && <ByTopic />}
      {tab === 'product' && <ByProduct />}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon className="w-8 h-8 text-slate-300" />}
      </div>
    </div>
  );
}

function ByEmployee() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trainingSummaryService.byEmployee().then(r => setData(r.data)).catch(() => toast.error('Errore')).finally(() => setLoading(false));
  }, []);

  const totalHours = data.reduce((a, d) => a + d.total_hours, 0);
  const totalCourses = data.reduce((a, d) => a + d.courses, 0);

  return (
    <div className="space-y-4" data-testid="by-employee">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Dipendenti formati" value={data.length} icon={Users} />
        <StatCard label="Corsi totali" value={totalCourses} icon={CalendarDays} />
        <StatCard label="Ore totali" value={totalHours.toFixed(1)} icon={Clock} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : data.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessun dato di formazione disponibile.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Dipendente</th><th className="px-4 py-3">Reparto</th><th className="px-4 py-3">Ruolo</th>
                <th className="px-4 py-3 text-right">Numero corsi</th><th className="px-4 py-3 text-right">Ore totali</th><th className="px-4 py-3">Ultima formazione</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(d => (
                  <tr key={d.employee_id} className="hover:bg-slate-50" data-testid={`emp-row-${d.employee_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{d.last_name} {d.first_name}</div>
                      <div className="text-xs text-slate-400 font-mono">{d.employee_code}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{d.department || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{d.role || '-'}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{d.courses}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{d.total_hours}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(d.last_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ByPeriod() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [year, setYear] = useState(new Date().getFullYear());

  const load = () => {
    setLoading(true);
    trainingSummaryService.byPeriod({ period, year }).then(r => setData(r.data)).catch(() => toast.error('Errore')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [period, year]);

  const totalHours = data.reduce((a, d) => a + d.total_hours, 0);
  const totalSessions = data.reduce((a, d) => a + d.sessions, 0);
  const years = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y);

  const fmtPeriod = (p) => {
    if (period === 'month') {
      const [y, m] = p.split('-');
      const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
      return `${months[parseInt(m) - 1]} ${y}`;
    }
    return p;
  };

  return (
    <div className="space-y-4" data-testid="by-period">
      <div className="flex items-center gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {['month', 'quarter', 'year'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${period === p ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
              {p === 'month' ? 'Mese' : p === 'quarter' ? 'Trimestre' : 'Anno'}
            </button>
          ))}
        </div>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg" data-testid="period-year">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Sessioni" value={totalSessions} icon={CalendarDays} />
        <StatCard label="Ore totali" value={totalHours.toFixed(1)} icon={Clock} />
        <StatCard label="Periodi attivi" value={data.length} icon={BarChart3} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : data.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessun dato per il periodo selezionato.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Periodo</th><th className="px-4 py-3 text-right">Sessioni</th><th className="px-4 py-3 text-right">Ore totali</th><th className="px-4 py-3 text-right">Dipendenti coinvolti</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(d => (
                  <tr key={d.period} className="hover:bg-slate-50" data-testid={`period-row-${d.period}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{fmtPeriod(d.period)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.sessions}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{d.total_hours}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.unique_employees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visual bar chart */}
      {data.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Ore formazione per periodo</h3>
          <div className="space-y-2">
            {data.map(d => {
              const maxH = Math.max(...data.map(x => x.total_hours), 1);
              const pct = (d.total_hours / maxH) * 100;
              return (
                <div key={d.period} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-24 text-right font-mono">{fmtPeriod(d.period)}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2" style={{ width: `${Math.max(pct, 5)}%` }}>
                      <span className="text-xs font-semibold text-white">{d.total_hours}h</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ByTopic() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trainingSummaryService.byTopic().then(r => setData(r.data)).catch(() => toast.error('Errore')).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="by-topic">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : data.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessun dato disponibile.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Tema</th><th className="px-4 py-3 text-right">Sessioni</th><th className="px-4 py-3 text-right">Ore totali</th><th className="px-4 py-3 text-right">Dipendenti coinvolti</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(d => (
                  <tr key={d.topic} className="hover:bg-slate-50" data-testid={`topic-row-${d.topic}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{d.topic}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.sessions}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{d.total_hours}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.unique_employees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visual */}
      {data.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Ore per tema formativo</h3>
          <div className="space-y-2">
            {data.map(d => {
              const maxH = Math.max(...data.map(x => x.total_hours), 1);
              const pct = (d.total_hours / maxH) * 100;
              return (
                <div key={d.topic} className="flex items-center gap-3">
                  <span className="text-xs text-slate-600 w-40 text-right truncate" title={d.topic}>{d.topic}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                    <div className="bg-emerald-500 h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2" style={{ width: `${Math.max(pct, 5)}%` }}>
                      <span className="text-xs font-semibold text-white">{d.total_hours}h</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ByProduct() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trainingSummaryService.byProduct().then(r => setData(r.data)).catch(() => toast.error('Errore')).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="by-product">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : data.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessuna formazione collegata a prodotti.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Prodotto</th><th className="px-4 py-3 text-right">Persone formate</th><th className="px-4 py-3 text-right">Ore erogate</th>
                <th className="px-4 py-3 text-right">Idonei</th><th className="px-4 py-3 text-right">Non idonei</th><th className="px-4 py-3 text-right">In affiancamento</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(d => (
                  <tr key={d.product_id} className="hover:bg-slate-50" data-testid={`product-row-${d.product_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{d.product_code}</div>
                      <div className="text-xs text-slate-400">{d.product_name}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{d.trained_people}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{d.total_hours}</td>
                    <td className="px-4 py-3 text-right"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">{d.idonei}</span></td>
                    <td className="px-4 py-3 text-right"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">{d.non_idonei}</span></td>
                    <td className="px-4 py-3 text-right"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{d.affiancamento}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
